const QR_API_BASE = 'https://api.qrserver.com/v1/create-qr-code/';
const SCAN_TEXT = 'For Nursing Service Scan Here.';
const ROOM_NO_PREFIX = 'Room No:';

const SMALL_VARIANT = {
    cardWidth: 280,
    qrSize: 190,
    logoWidth: 90,
    logoHeight: 90,
    paddingTop: 22,
    paddingBottom: 24,
    paddingHorizontal: 16,
    scanFontSize: 14,
    roomFontSize: 16,
    logoMarginBottom: 18,
    qrMarginBottom: 14,
    scanMarginBottom: 10,
};

// Measured from reference PDF "A4 - HDU 4" (595 x 842 pt) at 300 DPI.
const A4_WIDTH = 2480;
const A4_HEIGHT = 3508;
const A4_PDF_LAYOUT = {
    pageWidth: A4_WIDTH,
    pageHeight: A4_HEIGHT,
    borderInsetX: 70,
    borderTop: 400,
    borderBottom: 3104,
    borderWidth: 3,
    logoWidth: 392,
    logoHeight: 406,
    logoTop: 504,
    qrSize: 1294,
    qrTop: 1166,
    scanFontSize: 46,
    scanBaseline: 2788,
    roomFontSize: 58,
    roomBaseline: 2976,
};

const CARD_VARIANTS = {
    small: SMALL_VARIANT,
    a4: A4_PDF_LAYOUT,
};

const loadImage = (src) => new Promise((resolve, reject) => {
    const image = new Image();
    image.crossOrigin = 'anonymous';
    image.onload = () => resolve(image);
    image.onerror = reject;
    image.src = src;
});

export const buildInPatientRoomUrl = (room, meetURL) => {
    const vendorId = room && (room.vendorId || room.vendor_id);
    const encryptRoomId = room && room.encryptRoomId;
    const meetBase = meetURL.replace('custom/', '');

    if (vendorId) {
        return `${meetBase}SRM_IP/patient/${vendorId}`;
    }

    return `${meetBase}SRM_IP/custom/${encryptRoomId || ''}`;
};

export const buildRoomQrLabel = (room) => {
    const roomName = room && room.patientRoomName ? String(room.patientRoomName).trim() : '';
    if (roomName) {
        return roomName;
    }
    if (room && room.patientRoomId != null) {
        return String(room.patientRoomId);
    }
    return '-';
};

export const buildQrCodeImageUrl = (patientUrl, size = 250) => {
    return `${QR_API_BASE}?data=${encodeURIComponent(patientUrl)}&size=${size}x${size}&margin=20`;
};

export const scaleA4PdfLayout = (targetWidth) => {
    const scale = targetWidth / A4_PDF_LAYOUT.pageWidth;
    const scaled = {};
    Object.keys(A4_PDF_LAYOUT).forEach((key) => {
        scaled[key] = A4_PDF_LAYOUT[key] * scale;
    });
    scaled.pageWidth = targetWidth;
    scaled.pageHeight = A4_PDF_LAYOUT.pageHeight * scale;
    return scaled;
};

const getSmallCardHeight = (variantConfig) => (
    variantConfig.paddingTop
    + variantConfig.logoHeight
    + variantConfig.logoMarginBottom
    + variantConfig.qrSize
    + variantConfig.qrMarginBottom
    + variantConfig.scanFontSize + variantConfig.scanMarginBottom + 8
    + variantConfig.roomFontSize + 12
    + variantConfig.paddingBottom
);

const drawSmallCard = (ctx, {
    roomLabel,
    logoImage,
    qrImage,
    variantConfig,
    offsetX,
    offsetY,
}) => {
    const {
        cardWidth,
        qrSize,
        logoWidth,
        logoHeight,
        paddingTop,
        scanFontSize,
        roomFontSize,
        logoMarginBottom,
        qrMarginBottom,
        scanMarginBottom,
    } = variantConfig;

    const cardHeight = getSmallCardHeight(variantConfig);
    const centerX = offsetX + cardWidth / 2;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(offsetX, offsetY, cardWidth, cardHeight);

    ctx.strokeStyle = '#D9D9D9';
    ctx.lineWidth = 2;
    ctx.strokeRect(offsetX + 1, offsetY + 1, cardWidth - 2, cardHeight - 2);

    let currentY = offsetY + paddingTop;

    ctx.drawImage(logoImage, centerX - logoWidth / 2, currentY, logoWidth, logoHeight);
    currentY += logoHeight + logoMarginBottom;

    ctx.drawImage(qrImage, centerX - qrSize / 2, currentY, qrSize, qrSize);
    currentY += qrSize + qrMarginBottom;

    ctx.fillStyle = '#666666';
    ctx.font = `${scanFontSize}px Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.fillText(SCAN_TEXT, centerX, currentY + scanFontSize);
    currentY += scanFontSize + scanMarginBottom;

    ctx.fillStyle = '#000000';
    ctx.font = `700 ${roomFontSize}px Arial, sans-serif`;
    ctx.fillText(`${ROOM_NO_PREFIX} ${roomLabel}`, centerX, currentY + roomFontSize);

    return cardHeight;
};

const drawA4Page = (ctx, { roomLabel, logoImage, qrImage, layout }) => {
    const {
        pageWidth,
        pageHeight,
        borderInsetX,
        borderTop,
        borderBottom,
        borderWidth,
        logoWidth,
        logoHeight,
        logoTop,
        qrSize,
        qrTop,
        scanFontSize,
        scanBaseline,
        roomFontSize,
        roomBaseline,
    } = layout;

    const centerX = pageWidth / 2;

    ctx.fillStyle = '#FFFFFF';
    ctx.fillRect(0, 0, pageWidth, pageHeight);

    ctx.strokeStyle = '#D9D9D9';
    ctx.lineWidth = borderWidth;
    ctx.strokeRect(
        borderInsetX,
        borderTop,
        pageWidth - borderInsetX * 2,
        borderBottom - borderTop
    );

    ctx.drawImage(
        logoImage,
        centerX - logoWidth / 2,
        logoTop,
        logoWidth,
        logoHeight
    );

    ctx.drawImage(
        qrImage,
        centerX - qrSize / 2,
        qrTop,
        qrSize,
        qrSize
    );

    ctx.textAlign = 'center';
    ctx.fillStyle = '#666666';
    ctx.font = `${scanFontSize}px Arial, sans-serif`;
    ctx.fillText(SCAN_TEXT, centerX, scanBaseline);

    ctx.fillStyle = '#000000';
    ctx.font = `700 ${roomFontSize}px Arial, sans-serif`;
    ctx.fillText(`${ROOM_NO_PREFIX} ${roomLabel}`, centerX, roomBaseline);

    return pageHeight;
};

export const generateRoomQrCardCanvas = async ({
    patientUrl,
    roomLabel,
    variant = 'small',
    logoSrc = `${process.env.PUBLIC_URL || ''}/srm_qr_logo.png`,
}) => {
    const variantConfig = CARD_VARIANTS[variant] || CARD_VARIANTS.small;
    const qrImage = await loadImage(buildQrCodeImageUrl(patientUrl, variantConfig.qrSize));
    const logoImage = await loadImage(logoSrc);

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    if (variant === 'a4') {
        canvas.width = A4_WIDTH;
        canvas.height = A4_HEIGHT;
        drawA4Page(ctx, {
            roomLabel,
            logoImage,
            qrImage,
            layout: A4_PDF_LAYOUT,
        });
    } else {
        const cardHeight = getSmallCardHeight(variantConfig);
        canvas.width = variantConfig.cardWidth;
        canvas.height = cardHeight;
        drawSmallCard(ctx, {
            roomLabel,
            logoImage,
            qrImage,
            variantConfig,
            offsetX: 0,
            offsetY: 0,
        });
    }

    return canvas;
};

const canvasToBlob = (canvas, type = 'image/png', quality) => new Promise((resolve, reject) => {
    canvas.toBlob((blob) => {
        if (blob) {
            resolve(blob);
            return;
        }
        reject(new Error('Unable to create QR card image'));
    }, type, quality);
});

/** Single-page PDF with embedded JPEG (no extra library). */
export const buildPdfFromJpegBytes = (jpegBytes, imgWidthPx, imgHeightPx, pageWidthPt, pageHeightPt) => {
    const encoder = new TextEncoder();
    const jpeg = jpegBytes instanceof Uint8Array ? jpegBytes : new Uint8Array(jpegBytes);

    const objects = [];
    const addObject = (content) => {
        objects.push(content);
        return objects.length;
    };

    const catalogId = addObject('<< /Type /Catalog /Pages 2 0 R >>');
    const pagesId = addObject('<< /Type /Pages /Kids [3 0 R] /Count 1 >>');
    const pageId = addObject(
        `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${pageWidthPt} ${pageHeightPt}] `
        + `/Resources << /XObject << /Im0 5 0 R >> >> /Contents 4 0 R >>`
    );
    const contentStream = `q\n${pageWidthPt} 0 0 ${pageHeightPt} 0 0 cm\n/Im0 Do\nQ\n`;
    const contentId = addObject(
        `<< /Length ${contentStream.length} >>\nstream\n${contentStream}endstream`
    );
    const imageDict =
        `<< /Type /XObject /Subtype /Image /Width ${imgWidthPx} /Height ${imgHeightPx} `
        + `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode /Length ${jpeg.length} >>\n`
        + `stream\n`;
    // Image object stored as { dict, binary } for binary write
    const imageId = addObject({ dict: imageDict, binary: jpeg, end: '\nendstream' });

    void catalogId;
    void pagesId;
    void pageId;
    void contentId;
    void imageId;

    const parts = [];
    const pushStr = (str) => {
        parts.push(encoder.encode(str));
    };

    pushStr('%PDF-1.4\n');
    const offsets = [0];

    for (let i = 0; i < objects.length; i += 1) {
        offsets.push(parts.reduce((sum, p) => sum + p.length, 0));
        const obj = objects[i];
        pushStr(`${i + 1} 0 obj\n`);
        if (obj && obj.binary) {
            pushStr(obj.dict);
            parts.push(obj.binary);
            pushStr(obj.end);
            pushStr('\nendobj\n');
        } else {
            pushStr(`${obj}\nendobj\n`);
        }
    }

    const xrefOffset = parts.reduce((sum, p) => sum + p.length, 0);
    pushStr(`xref\n0 ${objects.length + 1}\n`);
    pushStr('0000000000 65535 f \n');
    for (let i = 1; i <= objects.length; i += 1) {
        pushStr(`${String(offsets[i]).padStart(10, '0')} 00000 n \n`);
    }
    pushStr(`trailer\n<< /Size ${objects.length + 1} /Root 1 0 R >>\n`);
    pushStr(`startxref\n${xrefOffset}\n%%EOF\n`);

    const total = parts.reduce((sum, p) => sum + p.length, 0);
    const out = new Uint8Array(total);
    let offset = 0;
    parts.forEach((part) => {
        out.set(part, offset);
        offset += part.length;
    });
    return out;
};

const getPdfPageSizePt = (variant, canvasWidth, canvasHeight) => {
    if (variant === 'a4') {
        return { pageWidthPt: 595.28, pageHeightPt: 841.89 };
    }
    // Keep small card aspect; ~96 CSS px ≈ 72 pt
    const scale = 72 / 96;
    return {
        pageWidthPt: Math.round(canvasWidth * scale * 100) / 100,
        pageHeightPt: Math.round(canvasHeight * scale * 100) / 100,
    };
};

const triggerBlobDownload = (blob, fileName) => {
    const blobUrl = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = blobUrl;
    link.download = fileName;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(blobUrl);
};

export const generateRoomQrCardBlob = async (options) => {
    const canvas = await generateRoomQrCardCanvas(options);
    return canvasToBlob(canvas, 'image/png');
};

export const downloadRoomQrCard = async ({
    patientUrl,
    roomLabel,
    downloadName,
    variant = 'small',
    format = 'png',
}) => {
    const canvas = await generateRoomQrCardCanvas({ patientUrl, roomLabel, variant });
    const safeName = String(downloadName || 'room').replace(/[^\w.-]+/g, '_');
    const suffix = variant === 'a4' ? 'A4' : 'Small';
    const fileFormat = String(format || 'png').toLowerCase() === 'pdf' ? 'pdf' : 'png';
    const fileName = `${safeName}_QRCode_${suffix}.${fileFormat}`;

    if (fileFormat === 'png') {
        const blob = await canvasToBlob(canvas, 'image/png');
        triggerBlobDownload(blob, fileName);
        return fileName;
    }

    const jpegBlob = await canvasToBlob(canvas, 'image/jpeg', 0.95);
    const jpegBuffer = new Uint8Array(await jpegBlob.arrayBuffer());
    const { pageWidthPt, pageHeightPt } = getPdfPageSizePt(variant, canvas.width, canvas.height);
    const pdfBytes = buildPdfFromJpegBytes(
        jpegBuffer,
        canvas.width,
        canvas.height,
        pageWidthPt,
        pageHeightPt
    );
    triggerBlobDownload(new Blob([pdfBytes], { type: 'application/pdf' }), fileName);
    return fileName;
};

export const getRoomQrCardStyles = (variant = 'small') => {
    if (variant === 'a4') {
        const layout = scaleA4PdfLayout(794);
        const borderWidth = Math.max(1, layout.borderWidth);
        return {
            page: {
                position: 'relative',
                width: `${layout.pageWidth}px`,
                height: `${layout.pageHeight}px`,
                backgroundColor: '#FFFFFF',
                boxSizing: 'border-box',
            },
            // Inset frame — same geometry as canvas download strokeRect
            frame: {
                position: 'absolute',
                left: `${layout.borderInsetX}px`,
                top: `${layout.borderTop}px`,
                width: `${layout.pageWidth - layout.borderInsetX * 2}px`,
                height: `${layout.borderBottom - layout.borderTop}px`,
                borderWidth: `${borderWidth}px`,
                borderColor: '#D9D9D9',
                borderStyle: 'solid',
                boxSizing: 'border-box',
                pointerEvents: 'none',
            },
            logo: {
                position: 'absolute',
                left: '50%',
                top: `${layout.logoTop}px`,
                width: `${layout.logoWidth}px`,
                height: `${layout.logoHeight}px`,
                transform: 'translateX(-50%)',
            },
            qr: {
                position: 'absolute',
                left: '50%',
                top: `${layout.qrTop}px`,
                width: `${layout.qrSize}px`,
                height: `${layout.qrSize}px`,
                transform: 'translateX(-50%)',
            },
            scanText: {
                position: 'absolute',
                left: '50%',
                top: `${layout.scanBaseline - layout.scanFontSize}px`,
                width: '100%',
                transform: 'translateX(-50%)',
                fontSize: `${layout.scanFontSize}px`,
                color: '#666666',
                textAlign: 'center',
                fontFamily: 'Arial, sans-serif',
            },
            roomText: {
                position: 'absolute',
                left: '50%',
                top: `${layout.roomBaseline - layout.roomFontSize}px`,
                width: '100%',
                transform: 'translateX(-50%)',
                fontSize: `${layout.roomFontSize}px`,
                fontWeight: '700',
                textAlign: 'center',
                fontFamily: 'Arial, sans-serif',
            },
        };
    }

    const config = CARD_VARIANTS.small;
    return {
        card: {
            width: `${config.cardWidth}px`,
            paddingTop: `${config.paddingTop}px`,
            paddingBottom: `${config.paddingBottom}px`,
            paddingLeft: `${config.paddingHorizontal}px`,
            paddingRight: `${config.paddingHorizontal}px`,
            borderWidth: '2px',
            borderColor: '#D9D9D9',
            borderStyle: 'solid',
            borderRadius: 0,
            backgroundColor: '#FFFFFF',
            boxSizing: 'border-box',
            textAlign: 'center',
        },
        logo: {
            width: `${config.logoWidth}px`,
            height: `${config.logoHeight}px`,
            marginBottom: `${config.logoMarginBottom}px`,
        },
        qr: {
            width: `${config.qrSize}px`,
            height: `${config.qrSize}px`,
            marginBottom: `${config.qrMarginBottom}px`,
        },
        scanText: {
            fontSize: `${config.scanFontSize}px`,
            marginBottom: `${config.scanMarginBottom}px`,
            color: '#666666',
            fontFamily: 'Arial, sans-serif',
        },
        roomText: {
            fontSize: `${config.roomFontSize}px`,
            fontWeight: '700',
            fontFamily: 'Arial, sans-serif',
        },
    };
};

export const A4_EXPORT_WIDTH = A4_WIDTH;
export const A4_EXPORT_HEIGHT = A4_HEIGHT;
export { A4_PDF_LAYOUT };
